import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site, Integration, MasterSlaveIntegration } from '@/types';

const MasterSlaveIntegrations: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<MasterSlaveIntegration | null>(null);
  const [formData, setFormData] = useState({
    site_id: '',
    master_system_id: '',
    slave_system_id: '',
    type: '',
    sync_frequency: '',
    active: false,
  });

  const queryClient = useQueryClient();

  // Fetch master slave integrations
  const { data: integrations = [], isLoading } = useQuery(
    'masterSlaveIntegrations',
    apiService.getMasterSlaveIntegrations
  );

  // Fetch sites
  const { data: sites = [] } = useQuery('sites', apiService.getSites);

  // Fetch system integrations
  const { data: systemIntegrations = [] } = useQuery('integrations', apiService.getIntegrations);

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: any) => {
      if (editingIntegration) {
        return apiService.updateMasterSlaveIntegration(editingIntegration.id, data);
      } else {
        return apiService.createMasterSlaveIntegration(data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('masterSlaveIntegrations');
        handleCloseModal();
      },
      onError: (error) => {
        console.error('Save failed:', error);
        alert('Failed to save integration');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(apiService.deleteMasterSlaveIntegration, {
    onSuccess: () => {
      queryClient.invalidateQueries('masterSlaveIntegrations');
    },
    onError: (error) => {
      console.error('Delete failed:', error);
      alert('Failed to delete integration');
    },
  });

  const syncFrequencyOptions = [
    { value: '', label: 'Select One' },
    { value: 'every_2_minutes', label: 'Every 2 mins' },
    { value: 'daily', label: 'Daily' },
  ];

  const synchronizationTypes = [
    { value: '', label: 'Select One' },
    // Products group
    { value: 'update_stock', label: 'Update stock', group: 'Products' },
    { value: 'product_create', label: 'Create Products', group: 'Products' },
    { value: 'price_update', label: 'Price Update', group: 'Products' },
    { value: 'reactivate_activate_products', label: 'Reactivate/Activate products', group: 'Products' },
    // Invoices/orders group
    { value: 'invoice_create', label: 'Create invoices', group: 'Invoices/orders' },
    { value: 'customer_sync', label: 'Sync customers', group: 'Invoices/orders' },
    { value: 'order_update', label: 'Update orders', group: 'Invoices/orders' },
    { value: 'order_delivery_state_listen', label: 'Delivery State Listener', group: 'Invoices/orders' },
    { value: 'order_create', label: 'Create orders', group: 'Invoices/orders' },
    { value: 'update_invoice_payment_status', label: 'Update Invoice payment status', group: 'Invoices/orders' },
    // Reconciliation group
    { value: 'transaction_to_invoice_payments', label: 'Match Payment transaction with invoices', group: 'Reconciliation' },
    { value: 'resync_failed_transaction_to_invoice_payments', label: 'Resync failed Payment transactions', group: 'Reconciliation' },
    { value: 'payout_to_accumulated_report_voucher', label: 'Create Payment report', group: 'Reconciliation' },
    { value: 'transaction_to_voucher', label: 'Create transaction vouchers', group: 'Reconciliation' },
    { value: 'payout_to_daily_sales_and_fees_vouchers', label: 'Book daily sales and fees', group: 'Reconciliation' },
  ];

  const handleOpenModal = (integration?: MasterSlaveIntegration) => {
    if (integration) {
      setEditingIntegration(integration);
      setFormData({
        site_id: integration.site_id?.toString() || '',
        master_system_id: integration.master_system_id?.toString() || '',
        slave_system_id: integration.slave_system_id?.toString() || '',
        type: integration.type || '',
        sync_frequency: integration.sync_frequency || '',
        active: integration.active || false,
      });
    } else {
      setEditingIntegration(null);
      setFormData({
        site_id: '',
        master_system_id: '',
        slave_system_id: '',
        type: '',
        sync_frequency: '',
        active: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingIntegration(null);
    setFormData({
      site_id: '',
      master_system_id: '',
      slave_system_id: '',
      type: '',
      sync_frequency: '',
      active: false,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      site_id: parseInt(formData.site_id),
      master_system_id: parseInt(formData.master_system_id),
      slave_system_id: parseInt(formData.slave_system_id),
    };

    saveMutation.mutate(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate(id);
    }
  };

  const getSiteName = (siteId: number) => {
    const site = sites.find((s: Site) => s.id === siteId);
    return site?.url || `Site ${siteId}`;
  };

  const getIntegrationName = (integrationId: number) => {
    const integration = systemIntegrations.find((i: Integration) => i.id === integrationId);
    return integration?.name || `Integration ${integrationId}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Master Slave Integrations</h1>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Add Master Slave Integration</span>
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Site</th>
                <th>Master System</th>
                <th>Slave System</th>
                <th>Type</th>
                <th>Frequency</th>
                <th>Latest Update</th>
                <th>Active</th>
                <th>Update</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((integration: MasterSlaveIntegration) => (
                <tr key={integration.id}>
                  <td className="font-mono text-sm">{integration.id}</td>
                  <td>{getSiteName(integration.site_id)}</td>
                  <td>{getIntegrationName(integration.master_system_id)}</td>
                  <td>{getIntegrationName(integration.slave_system_id)}</td>
                  <td>
                    <span className="badge badge-secondary">
                      {integration.type}
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {integration.sync_frequency}
                    </span>
                  </td>
                  <td className="text-sm text-gray-600">
                    {integration.latest_update || 'Never'}
                  </td>
                  <td>
                    <span className={`badge ${integration.active ? 'badge-success' : 'badge-danger'}`}>
                      {integration.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${integration.update ? 'badge-success' : 'badge-secondary'}`}>
                      {integration.update ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(integration)}
                        className="btn-icon btn-icon-secondary"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(integration.id)}
                        className="btn-icon btn-icon-danger"
                        title="Delete"
                        disabled={deleteMutation.isLoading}
                      >
                        {deleteMutation.isLoading ? (
                          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingIntegration ? `Edit Integration #${editingIntegration.id}` : 'New Master Slave Integration'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-section">
                <div className="form-row">
                  <div className="form-column">
                    <div>
                      <label className="form-label form-label-required">Site:</label>
                      <select
                        value={formData.site_id}
                        onChange={(e) => handleInputChange('site_id', e.target.value)}
                        className="form-input"
                        required
                      >
                        <option value="">Select Site</option>
                        {sites.map((site: Site) => (
                          <option key={site.id} value={site.id}>
                            {site.url}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="form-label form-label-required">Master System:</label>
                      <select
                        value={formData.master_system_id}
                        onChange={(e) => handleInputChange('master_system_id', e.target.value)}
                        className="form-input"
                        required
                      >
                        <option value="">Select Master System</option>
                        {systemIntegrations.map((integration: Integration) => (
                          <option key={integration.id} value={integration.id}>
                            {integration.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="form-label form-label-required">Slave System:</label>
                      <select
                        value={formData.slave_system_id}
                        onChange={(e) => handleInputChange('slave_system_id', e.target.value)}
                        className="form-input"
                        required
                      >
                        <option value="">Select Slave System</option>
                        {systemIntegrations.map((integration: Integration) => (
                          <option key={integration.id} value={integration.id}>
                            {integration.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-column">
                    <div>
                      <label className="form-label form-label-required">Type:</label>
                      <select
                        value={formData.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="form-input"
                        required
                      >
                        {synchronizationTypes.map((type) => {
                          if (type.group) {
                            // This will be handled by the optgroup logic below
                            return null;
                          }
                          return (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          );
                        })}

                        {/* Products group */}
                        <optgroup label="Products">
                          {synchronizationTypes
                            .filter(type => type.group === 'Products')
                            .map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                        </optgroup>

                        {/* Invoices/orders group */}
                        <optgroup label="Invoices/orders">
                          {synchronizationTypes
                            .filter(type => type.group === 'Invoices/orders')
                            .map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                        </optgroup>

                        {/* Reconciliation group */}
                        <optgroup label="Reconciliation">
                          {synchronizationTypes
                            .filter(type => type.group === 'Reconciliation')
                            .map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                        </optgroup>
                      </select>
                    </div>

                    <div>
                      <label className="form-label form-label-required">Frequency:</label>
                      <select
                        value={formData.sync_frequency}
                        onChange={(e) => handleInputChange('sync_frequency', e.target.value)}
                        className="form-input"
                        required
                      >
                        {syncFrequencyOptions.map((freq) => (
                          <option key={freq.value} value={freq.value}>
                            {freq.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center pt-4">
                      <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) => handleInputChange('active', e.target.checked)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <label htmlFor="active" className="ml-2 text-sm font-medium text-gray-700">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isLoading}
                    className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                  >
                    {saveMutation.isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterSlaveIntegrations;
